/**
 * CallBar — Draggable floating phone widget shown during active calls.
 *
 * Displays: contact number, timer (MM:SS), status text, action buttons.
 * Supports multiple simultaneous calls via tab strip.
 * Auto-dismisses 3s after call ends.
 * Draggable — user can position it anywhere on screen.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Phone,
  PhoneOff,
  PhoneForwarded,
  Mic,
  MicOff,
  Pause,
  Play,
  Circle,
  Hash,
  X,
  GripHorizontal,
  Loader2,
  MessageCircle,
  Minimize2,
  Maximize2,
  UserPlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallStore } from '@/stores/call-store';
import { CALL_STATUS_CONFIGS, isCallBarCall } from '@/types/voice';
import { CallNotes } from './call-notes';
import { Keypad } from './keypad';
import { postCallHostDrag } from '@/hooks/use-appcursor-call-bridge';

/** Format seconds to MM:SS */
function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Plain-words call error. Twilio's mic error (31401) reads like a stack trace to an agent. */
function callErrorText(message: string): string {
  if (/31401|31208|NotAllowedError|PermissionDenied/i.test(message)) {
    return 'Microphone is blocked. Allow the microphone for this site in your browser, then try again.';
  }
  return message;
}

/**
 * `embedded` = the AppCursor call host frame (see use-appcursor-call-bridge.ts):
 * the frame is sized to the bar and placed by the extension, so the bar sits
 * in normal flow with a fixed width. Dragging it asks the extension to move
 * the frame (postCallHostDrag).
 */
export function CallBar({ embedded = false }: { embedded?: boolean } = {}) {
  const activeCalls = useCallStore((s) => s.activeCalls);
  const muteCall = useCallStore((s) => s.muteCall);
  const holdCall = useCallStore((s) => s.holdCall);
  const endCall = useCallStore((s) => s.endCall);
  const toggleRecording = useCallStore((s) => s.toggleRecording);
  const sendDigits = useCallStore((s) => s.sendDigits);
  const transferCall = useCallStore((s) => s.transferCall);
  const addCallParticipant = useCallStore((s) => s.addCallParticipant);
  const removeCallParticipant = useCallStore((s) => s.removeCallParticipant);

  // Track which call is selected when multiple calls exist
  const [selectedCallIdx, setSelectedCallIdx] = useState(0);
  const [showKeypad, setShowKeypad] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [showConference, setShowConference] = useState(false);
  const [conferenceTarget, setConferenceTarget] = useState('');
  const dismissedCalls = useCallStore((s) => s.dismissedCallIds);
  const dismissCall = useCallStore((s) => s.dismissCall);
  const [minimized, setMinimized] = useState(false);

  // Dragging state
  const [position, setPosition] = useState({ x: -1, y: -1 }); // -1 = not positioned yet
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize position — top-right on wide screens, centered on narrow
  useEffect(() => {
    if (position.x === -1) {
      const vw = window.innerWidth;
      const widgetWidth = 340;
      setPosition({
        x: vw <= widgetWidth + 40 ? Math.max(0, (vw - widgetWidth) / 2) : vw - widgetWidth - 20,
        y: 60,
      });
    }
  }, [position.x]);

  // Keep widget within viewport bounds when it resizes (e.g. notes expand)
  useEffect(() => {
    if (embedded || !containerRef.current || position.x === -1) return;
    const rect = containerRef.current.getBoundingClientRect();
    const maxY = window.innerHeight - rect.height - 10;
    if (position.y > maxY && maxY > 0) {
      setPosition((p) => ({ ...p, y: Math.max(0, maxY) }));
    }
  });

  // Drag handlers
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input, textarea, a, select')) return; // Don't drag from interactive elements
    dragging.current = true;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const newX = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 50, e.clientY - dragOffset.current.y));
      setPosition({ x: newX, y: newY });
    };
    const onMouseUp = () => {
      dragging.current = false;
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Embedded drag: pointer capture keeps the moves coming while the frame
  // slides under the cursor; screen coordinates don't shift with the frame.
  const embeddedDragStart = useRef<{ x: number; y: number } | null>(null);
  const onEmbeddedPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, input, textarea, a, select')) return;
    embeddedDragStart.current = { x: e.screenX, y: e.screenY };
    e.currentTarget.setPointerCapture(e.pointerId);
    postCallHostDrag('start');
    e.preventDefault();
  }, []);
  const onEmbeddedPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = embeddedDragStart.current;
    if (start) postCallHostDrag('move', e.screenX - start.x, e.screenY - start.y);
  }, []);
  const onEmbeddedPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = embeddedDragStart.current;
    if (!start) return;
    embeddedDragStart.current = null;
    postCallHostDrag('end', e.screenX - start.x, e.screenY - start.y);
  }, []);
  const dragProps = embedded
    ? {
        onPointerDown: onEmbeddedPointerDown,
        onPointerMove: onEmbeddedPointerMove,
        onPointerUp: onEmbeddedPointerUp,
        onPointerCancel: onEmbeddedPointerUp,
      }
    : { onMouseDown };

  // Filter out dismissed calls and inbound ringing calls (handled by incoming toast).
  // A call that failed (e.g. microphone blocked) stays even if a cancel follows,
  // so the agent can read why.
  const visibleCalls = useMemo(
    () => activeCalls.filter((c) => isCallBarCall(c, dismissedCalls)),
    [activeCalls, dismissedCalls]
  );

  // Auto-expand and show notes once, when a call first connects. Re-checking on
  // every render undid Minimize within a second: the duration tick replaces
  // activeCalls each second.
  const autoExpandedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const newlyConnected = visibleCalls.filter(
      (c) => c.status === 'connected' && !autoExpandedIdsRef.current.has(c.id)
    );
    if (newlyConnected.length === 0) return;
    newlyConnected.forEach((c) => autoExpandedIdsRef.current.add(c.id));
    setMinimized(false);
    setShowNotes(true);
  }, [visibleCalls]);


  // Auto-dismiss ended calls (2s for answered-elsewhere, 3s for others).
  // Failed calls stay until the agent closes them — 3 s is too short to read
  // the error and act on it.
  // Timers live in a ref keyed by call id: the deps change every second
  // (duration ticks replace the activeCalls array), so effect-scoped timers
  // would be cleared and re-armed each tick and never actually fire.
  const dismissTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    for (const call of activeCalls) {
      const cfg = CALL_STATUS_CONFIGS[call.status];
      // Inbound ringing/canceled calls are never rendered (visibleCalls filters
      // them) — don't park them in dismissedCallIds, or a later legitimate
      // status (e.g. canceled → answered-elsewhere) would be born dismissed.
      const hidden = call.direction === 'inbound' && (call.status === 'ringing' || call.status === 'canceled');
      if (call.errorMessage) continue;
      if (cfg.callEnded && !hidden && !dismissedCalls.has(call.id) && !dismissTimersRef.current.has(call.id)) {
        const delay = call.status === 'answered-elsewhere' ? 2000 : 3000;
        const timer = setTimeout(() => {
          dismissTimersRef.current.delete(call.id);
          dismissCall(call.id);
        }, delay);
        dismissTimersRef.current.set(call.id, timer);
      }
    }
  }, [activeCalls, dismissedCalls, dismissCall]);

  // Clear any pending dismiss timers on unmount only
  useEffect(() => {
    const timers = dismissTimersRef.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  // Keep selectedCallIdx in bounds
  useEffect(() => {
    if (selectedCallIdx >= visibleCalls.length && visibleCalls.length > 0) {
      setSelectedCallIdx(visibleCalls.length - 1);
    }
  }, [visibleCalls.length, selectedCallIdx]);

  const activeCall = visibleCalls[selectedCallIdx];

  // Reset per-call panels when the shown call changes — otherwise a stale
  // transfer target or open keypad survives into the next call. Tracked via a
  // ref so a transient out-of-bounds render (a lower-indexed call being
  // dismissed makes activeCall momentarily undefined before the index clamps)
  // doesn't wipe the panels of the call the user is still on.
  const activeCallId = activeCall?.id;
  const lastPanelCallIdRef = useRef<string | undefined>(activeCallId);
  useEffect(() => {
    if (!activeCallId || lastPanelCallIdRef.current === activeCallId) return;
    lastPanelCallIdRef.current = activeCallId;
    setShowTransfer(false);
    setTransferTarget('');
    setShowKeypad(false);
  }, [activeCallId]);

  const handleDigit = useCallback(
    (digit: string) => {
      if (activeCall) {
        sendDigits(activeCall.id, digit);
      }
    },
    [activeCall?.id, sendDigits]
  );

  if (!activeCall || visibleCalls.length === 0) return null;

  const statusConfig = CALL_STATUS_CONFIGS[activeCall.status];
  const showOptions = new Set(statusConfig.showOptions);

  // Minimized view — small pill
  if (minimized) {
    return (
      <div
        ref={containerRef}
        {...dragProps}
        style={embedded ? undefined : { left: position.x, top: position.y }}
        className={embedded ? 'w-max cursor-grab active:cursor-grabbing' : 'fixed z-50 cursor-grab active:cursor-grabbing'}
      >
        <div
          className={cn(
            'flex items-center gap-2 rounded-full px-3 py-2 shadow-2xl border',
            statusConfig.callStarted
              ? 'bg-emerald-600 border-emerald-500 text-white'
              : statusConfig.callEnded
                ? 'bg-slate-700 border-slate-600 text-slate-300'
                : 'bg-blue-600 border-blue-500 text-white'
          )}
        >
          <Phone className="h-3.5 w-3.5" />
          {statusConfig.callStarted && (
            <span className="text-xs tabular-nums font-medium">{formatDuration(activeCall.duration)}</span>
          )}
          {!statusConfig.callStarted && (
            <span className="text-xs font-medium">{statusConfig.displayText}</span>
          )}
          <button
            onClick={() => setMinimized(false)}
            className="rounded-full p-0.5 hover:bg-white/20 transition-colors"
          >
            <Maximize2 className="h-3 w-3" />
          </button>
          {showOptions.has('end') && (
            <button
              onClick={() => endCall(activeCall.id)}
              className="rounded-full p-1 bg-red-500 hover:bg-red-600 transition-colors"
            >
              <PhoneOff className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      {...dragProps}
      style={embedded ? undefined : { left: position.x, top: position.y }}
      className={embedded ? 'w-max cursor-grab active:cursor-grabbing select-none' : 'fixed z-50 cursor-grab active:cursor-grabbing select-none'}
    >
      {/* Embedded: fixed width — the frame is sized from the bar, so a 100vw-based width would shrink it in a loop */}
      <div className={cn(embedded ? 'w-[340px]' : 'w-[min(340px,calc(100vw-16px))]', 'rounded-2xl bg-slate-800 dark:bg-slate-900 border border-slate-700 dark:border-slate-800 shadow-2xl overflow-hidden')}>
        {/* Drag handle + minimize + close */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-700/50">
          <GripHorizontal className="h-3.5 w-3.5 text-slate-500" />
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMinimized(true)}
              className="rounded p-0.5 text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
              title="Minimize"
            >
              <Minimize2 className="h-3 w-3" />
            </button>
            <button
              onClick={() => dismissCall(activeCall.id)}
              className="rounded p-0.5 text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
              title="Close"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Multi-call tabs */}
        {visibleCalls.length > 1 && (
          <div className="flex items-center gap-1 px-3 pt-1">
            {visibleCalls.map((c, idx) => (
              <button
                key={c.id}
                onClick={() => setSelectedCallIdx(idx)}
                className={cn(
                  'rounded-t-lg px-3 py-1 text-xs font-medium transition-colors',
                  idx === selectedCallIdx
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                )}
              >
                {c.customerNumber || 'Call'} {idx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Call info */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                statusConfig.callEnded
                  ? 'bg-slate-600'
                  : statusConfig.callStarted
                    ? 'bg-emerald-500/20'
                    : 'bg-blue-500/20'
              )}
            >
              <Phone
                className={cn(
                  'h-5 w-5',
                  statusConfig.callEnded
                    ? 'text-slate-400'
                    : statusConfig.callStarted
                      ? 'text-emerald-400'
                      : 'text-blue-400'
                )}
              />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white truncate flex items-center gap-1.5">
                {activeCall.callChannel === 'whatsapp' && (
                  <MessageCircle className="h-3.5 w-3.5 text-green-400 shrink-0" />
                )}
                {activeCall.customerNumber?.replace('whatsapp:', '') || 'Unknown'}
              </p>
              <div className="flex items-center gap-2">
                {activeCall.callChannel === 'whatsapp' && (
                  <span className="text-[10px] text-green-400 font-medium">WhatsApp</span>
                )}
                <span
                  className={cn(
                    'text-xs',
                    activeCall.errorMessage
                      ? 'text-red-400'
                      : statusConfig.callEnded
                        ? 'text-slate-400'
                        : statusConfig.callStarted
                          ? 'text-emerald-400'
                          : 'text-blue-400'
                  )}
                >
                  {activeCall.errorMessage ? 'Call failed' : statusConfig.displayText}
                </span>
                {statusConfig.callStarted && (
                  <span className="text-sm text-white tabular-nums font-medium">
                    {formatDuration(activeCall.duration)}
                  </span>
                )}
              </div>
              {/* Full error, wrapped — a cut-off message can't tell the agent what to fix */}
              {activeCall.errorMessage && (
                <p className="mt-1 text-xs text-red-300 break-words">{callErrorText(activeCall.errorMessage)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-1 px-3 pb-3">
          {showOptions.has('mute') && (
            <button
              onClick={() => muteCall(activeCall.id)}
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center transition-colors',
                activeCall.isMuted
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
              )}
              title={activeCall.isMuted ? 'Unmute' : 'Mute'}
            >
              {activeCall.isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}

          {showOptions.has('hold') && (
            <button
              onClick={() => holdCall(activeCall.id)}
              disabled={activeCall.holdPending}
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center transition-colors',
                activeCall.isOnHold
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white',
                activeCall.holdPending && 'opacity-60 cursor-not-allowed'
              )}
              title={activeCall.isOnHold ? 'Resume' : 'Hold'}
            >
              {activeCall.holdPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : activeCall.isOnHold ? (
                <Play className="h-4 w-4" />
              ) : (
                <Pause className="h-4 w-4" />
              )}
            </button>
          )}

          {showOptions.has('record') && (
            <button
              onClick={() => toggleRecording(activeCall.id)}
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center transition-colors',
                activeCall.isRecording
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
              )}
              title={activeCall.isRecording ? 'Stop Recording' : 'Record'}
            >
              <Circle className={cn('h-4 w-4', activeCall.isRecording && 'fill-current')} />
            </button>
          )}

          {showOptions.has('transfer') && (
            <button
              onClick={() => { setShowTransfer((v) => !v); setShowKeypad(false); setShowConference(false); }}
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center transition-colors',
                showTransfer
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
              )}
              title="Transfer"
            >
              <PhoneForwarded className="h-4 w-4" />
            </button>
          )}

          {showOptions.has('conference') && (
            <button
              onClick={() => { setShowConference((v) => !v); setShowKeypad(false); setShowTransfer(false); }}
              data-testid="call-add-person"
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center transition-colors',
                showConference
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
              )}
              title="Add person (conference call)"
            >
              {activeCall.addingParticipant
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <UserPlus className="h-4 w-4" />}
            </button>
          )}

          {showOptions.has('keypad') && (
            <button
              onClick={() => { setShowKeypad((v) => !v); setShowTransfer(false); setShowConference(false); }}
              className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center transition-colors',
                showKeypad
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
              )}
              title="Keypad"
            >
              <Hash className="h-4 w-4" />
            </button>
          )}



          {showOptions.has('end') && (
            <button
              onClick={() => endCall(activeCall.id)}
              className="h-10 w-10 rounded-full flex items-center justify-center bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              title="End Call"
            >
              <PhoneOff className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Keypad overlay */}
        {showKeypad && showOptions.has('keypad') && (
          <div className="px-4 pb-3 border-t border-slate-700">
            <div className="flex items-center justify-between py-2">
              <span className="text-[10px] font-medium text-slate-400 uppercase">Keypad</span>
              <button
                onClick={() => setShowKeypad(false)}
                className="rounded-full p-1 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <Keypad onDigit={handleDigit} compact enableKeyboard className="mx-auto w-fit" />
          </div>
        )}

        {/* Transfer overlay */}
        {showTransfer && showOptions.has('transfer') && (
          <div className="px-4 pb-3 border-t border-slate-700">
            <div className="flex items-center justify-between py-2">
              <span className="text-[10px] font-medium text-slate-400 uppercase">Transfer</span>
              <button
                onClick={() => setShowTransfer(false)}
                className="rounded-full p-1 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="space-y-2">
              <input
                type="text"
                value={transferTarget}
                onChange={(e) => setTransferTarget(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && transferTarget.trim()) {
                    transferCall(activeCall.id, transferTarget.trim());
                    setShowTransfer(false);
                    setTransferTarget('');
                  }
                }}
                placeholder="Phone number or agent email"
                className="w-full rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500">Cold transfer</p>
                <button
                  onClick={() => {
                    if (transferTarget.trim()) {
                      transferCall(activeCall.id, transferTarget.trim());
                      setShowTransfer(false);
                      setTransferTarget('');
                    }
                  }}
                  disabled={!transferTarget.trim()}
                  className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  Transfer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conference overlay — add a person, the agent stays on the call */}
        {showConference && showOptions.has('conference') && (
          <div className="px-4 pb-3 border-t border-slate-700">
            <div className="flex items-center justify-between py-2">
              <span className="text-[10px] font-medium text-slate-400 uppercase">Add person</span>
              <button
                onClick={() => setShowConference(false)}
                className="rounded-full p-1 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="space-y-2">
              <input
                type="text"
                value={conferenceTarget}
                onChange={(e) => setConferenceTarget(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && conferenceTarget.trim() && !activeCall.addingParticipant) {
                    addCallParticipant(activeCall.id, conferenceTarget.trim());
                    setShowConference(false);
                    setConferenceTarget('');
                  }
                }}
                placeholder="Phone number or agent email"
                className="w-full rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500">You stay on the call</p>
                <button
                  onClick={() => {
                    if (conferenceTarget.trim()) {
                      addCallParticipant(activeCall.id, conferenceTarget.trim());
                      setShowConference(false);
                      setConferenceTarget('');
                    }
                  }}
                  disabled={!conferenceTarget.trim() || !!activeCall.addingParticipant}
                  className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* People added to this call (conference call) */}
        {!statusConfig.callEnded && (activeCall.conferenceParticipants?.length ?? 0) > 0 && (
          <div className="px-4 pb-3 border-t border-slate-700" data-testid="call-participants">
            <div className="py-2">
              <span className="text-[10px] font-medium text-slate-400 uppercase">On this call</span>
            </div>
            <ul className="space-y-1">
              {activeCall.conferenceParticipants!.map((p) => (
                <li key={p.callSid} className="flex items-center gap-2 rounded-md bg-slate-700/40 px-2 py-1.5">
                  <span className="flex-1 min-w-0 truncate text-xs text-white" title={p.target}>{p.target}</span>
                  <span className={cn('text-[10px]', p.status === 'joined' ? 'text-emerald-400' : 'text-slate-400')}>
                    {p.status === 'joined' ? 'Joined' : 'Ringing…'}
                  </span>
                  <button
                    onClick={() => removeCallParticipant(activeCall.id, p.callSid)}
                    className="rounded-full p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700 transition-colors"
                    title={p.status === 'joined' ? 'Remove from call' : 'Cancel'}
                  >
                    <PhoneOff className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Notes panel — always visible */}
        {activeCall.tpaCallId && (
          <div className="px-4 pb-3 border-t border-slate-700">
            <div className="py-2">
              <span className="text-[10px] font-medium text-slate-400 uppercase">Notes</span>
            </div>
            <CallNotes
              callSid={activeCall.tpaCallId}
              customerNumber={activeCall.customerNumber}
              ownLeg
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
}
